/**
 * PAPPO — compressão de imagem no cliente.
 *
 * Redimensiona a imagem (lado maior ≈ 1600px) e recomprime para JPEG antes do
 * pré-visualização e do upload. Fotos da câmera traseira do celular chegam a
 * vários MB; segurar esse blob cru para o preview + enviá-lo por multipart
 * gera pressão de memória e faz a webview recarregar a página (perdendo a
 * foto). Reduzir para ~1600px/JPEG corta memória E acelera o upload.
 *
 * Nunca lança: qualquer falha de decodificação (ex.: HEIC não suportado pelo
 * canvas) devolve o arquivo ORIGINAL, para nunca bloquear o envio.
 */

/** Maior lado da imagem final, em pixels. */
const MAX_EDGE = 1600;
/** Qualidade do JPEG de saída (0–1). */
const QUALITY = 0.8;
/** Abaixo disto já é pequena o bastante — não vale re-encodar. */
const SKIP_UNDER_BYTES = 1_000_000;

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  close(): void;
}

/** Decodifica o arquivo com createImageBitmap (eficiente) e cai para <img>. */
async function decode(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(file);
    return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Falha ao decodificar imagem'));
      img.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => {} };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Comprime/redimensiona uma imagem no cliente. Devolve um novo File JPEG, ou o
 * próprio arquivo original quando não há ganho (já pequena) ou se algo falhar.
 */
export async function compressImage(file: File): Promise<File> {
  // Só imagens rasterizáveis. SVG/HEIC podem não decodificar → cai no catch.
  if (!file.type.startsWith('image/')) return file;

  try {
    const img = await decode(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));

    // Já cabe no limite e é leve → não recomprime (evita perda inútil).
    if (scale === 1 && file.size <= SKIP_UNDER_BYTES) {
      img.close();
      return file;
    }

    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      img.close();
      return file;
    }
    ctx.drawImage(img.source, 0, 0, w, h);
    img.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY)
    );
    // Libera o canvas (ajuda o GC em webviews com pouca memória).
    canvas.width = 0;
    canvas.height = 0;

    if (!blob || blob.size >= file.size) return file; // sem ganho → original

    const base = (file.name || 'foto').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
